/**
 * Every number the sim core needs that the spec leaves as tuning.
 *
 * All of these are PROVISIONAL. The spec fixes the shapes — deltas clamp,
 * decaying memories fade "for weeks", needs are "trivial when up, brutal
 * when down" — and deliberately not the curves (§21). Tests assert on the
 * shape of behaviour, never on these exact values, so they can move.
 *
 * Nothing in here is a rejected design (§22): there are no action slots, no
 * XP, no health bar. These are costs, caps and thresholds.
 */

export const TUNING = {
  /** §11 "liked that" axes and §14 "small, capped per scene". */
  relationship: {
    min: -100,
    max: 100,
    /** Largest move on one axis, per NPC, per scene, summed over outcomes. */
    perSceneCap: 10,
  },

  /** §19 model layer: how much of the record rides along in a prompt. */
  render: {
    /** Recent events the manifest lists by id, so outcomes can point at real ones. */
    recentEvents: 12,
    /** Malformed output is retried, never trusted (§19). Attempts in total. */
    attempts: 3,
    /** Reactions the nightly digest is built from. The day's loudest, not all of them. */
    digestReactions: 10,
  },

  /** §14 `stance_change`. */
  stance: {
    min: -100,
    max: 100,
    perSceneCap: 20,
  },

  /** §11 decay tier and §11 "the only lossy layer". */
  memory: {
    /** "Remembered for weeks." A decaying memory fades this long after it formed. */
    decayDays: 21,
    /** "Twice, and it promotes." Reinforcements needed before promotion. */
    promoteAfterReinforcements: 1,
    /** Narrative texture is lossy: keep only the newest this-many lines. */
    textureCap: 200,
  },

  /** §9 the needs floor. Minutes since the need was last met. */
  needs: {
    hungryAfter: 8 * 60,
    starvingAfter: 20 * 60,
    tiredAfter: 16 * 60,
    exhaustedAfter: 26 * 60,
    /**
     * Sleeping rough counts, but you wake as though you'd already been up
     * this long. Homelessness is a situation, not a resting state.
     */
    roughSleepPenalty: 6 * 60,
    /**
     * Rough sleep isn't a duration the player picks — it's drawn from the
     * seeded RNG (sim/reducer.ts's `sleep()`), because nobody on a stoop
     * controls how long they're actually out. 1.5-4 hours: shorter than any
     * bed sleep a player would reasonably request, fragmented and unsafe,
     * and it still stacks with `roughSleepPenalty` above for the "worse than
     * a full night" the caller pays on top of the short duration itself.
     */
    roughSleepMinMinutes: 90,
    roughSleepMaxMinutes: 240,
    mealMinutes: 30,
    /** 1970s dollars, before the price index. */
    mealPrice: 2,
  },

  /** §12 read-layer confidence = f(NPC opacity, time spent together). */
  read: {
    /** Minutes in each other's company before exposure stops adding confidence. */
    fullConfidenceExposure: 10 * 60,
    /** How much a perfect liar (deceptionCapacity 1) suppresses confidence. */
    opacityWeight: 0.7,
    /** Below this confidence, you can't tell anything: one vague line. */
    blind: 0.08,
    /** Below this, everything is hedged. Above, flat statements. */
    hazy: 0.4,
    /** An axis smaller than this in magnitude is not worth mentioning. */
    faint: 10,
  },

  /** §14 conversation. With the model off, a talk is one of these two lengths. */
  talk: {
    /** "Two minutes on the street is nothing." */
    wordMinutes: 2,
    /** "An hour in a back room is an hour." */
    sitMinutes: 60,
  },

  /** §6 the opening problem. */
  opening: {
    /** "Dirty" means something has to bend, not perfect play: cap at this share of legal-plus-illegal reach. */
    dirtyShareOfReach: 0.7,
  },

  /** §16 walking. Map units are whatever the district's SVG uses. */
  travel: {
    minutesPerMapUnit: 1,
    /** Even crossing the street costs something, or the world can't move (§9). */
    minimumMinutes: 1,
  },

  /** §15 a meeting is kept if you're there within this window, either side. */
  appointments: {
    graceMinutes: 30,
    /**
     * How close to the stated time counts as "it's happening right now" for
     * the model's benefit (sim/retrieval.ts's `appointmentTiming`) — the bug
     * this exists for: "I showed up near 6 and he didn't acknowledge it."
     * Deliberately narrower than `graceMinutes` (which only governs when the
     * sim itself calls a meeting kept or missed): a window this size still
     * leaves room for a distinct "late but not yet blown" state between it
     * and the grace deadline.
     */
    dueNowWindowMinutes: 10,
  },

  /** §11 "~1500 tokens of tightly scoped state" — expressed as counts. */
  retrieval: {
    decayingMemories: 8,
    recentInteractions: 5,
    recentlyHeard: 5,
    heardWithinDays: 7,
    texture: 3,
    facts: 12,
  },

  /** §13 how much one violent event adds to a grievance. */
  grievance: {
    perViolentEvent: 1,
  },

  /**
   * §17 violence. Beat resolution weights. Stats set the floor, situation
   * sets difficulty; none of these is a damage number.
   */
  violence: {
    /** Exchanges before both sides step back if nothing has settled it. */
    maxBeats: 4,
    minutesPerBeat: 1,
    /** Blows on the ground before someone is staggered, then down. */
    blowsToStagger: 1,
    blowsToDown: 2,
    /** Relationship movement at the end, per scene, before the §14 caps. */
    fear: { menace: 5, blows: 8, witness: 3 },
    warmth: { target: -6, witness: -2 },
    trust: { target: -6, witness: -2 },
  },

  /**
   * §17 injury as a state with a cost tail.
   *
   * OPEN (§21): injury duration and recovery curves are an open item. These
   * are placeholders so the slice runs, chosen to make the tail felt (a
   * serious injury keeps you off work for two weeks). They are not the
   * decision.
   */
  injury: {
    minor: { days: 3 },
    serious: { days: 14, bodyDrop: 1 },
    disabling: { days: 45, bodyDrop: 2 },
  },
} as const;
